import type { Item } from '@cheqroom/api';
import { useMemo } from 'react';

import { createCheckoutPermissions } from './use-checkout-permissions.hook';
import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';
import { createReservationPermissions } from './use-reservation-permissions.hook';
import {
	canAdminItem,
	canGiveItemCustody,
	canReleaseItemCustody,
	canTakeItemCustody,
	canTransferItemCustody,
	canUpdateItemGeo,
} from './utils/item';
import { canAdminKit } from './utils/kit';

export const createItemPermissions = (permissions: ReturnType<typeof usePermissions>) => {
	const { hasAnyPermission, useImport, useExport, isOwn, useReleaseAtLocation, useFlags, useDepreciations } =
		permissions;

	const { canReserve } = createReservationPermissions(permissions);
	const { canCheckout } = createCheckoutPermissions(permissions);

	const canAdmin = () => canAdminItem(permissions);

	const itemPermissions = {
		canRead: () => hasAnyPermission('ITEMS_READER', 'ITEMS_READER_RESTRICTED'),
		canCreate: canAdmin,
		canUpdate: canAdmin,
		// python-api `changeLocation`: ITEMS_LOCATION_ADMIN*, not the item admin right.
		canChangeLocation: () => hasAnyPermission('ITEMS_LOCATION_ADMIN', 'ITEMS_LOCATION_ADMIN_RESTRICTED'),
		canChangeCategory: canAdmin,
		canImport: () => useImport && hasAnyPermission('ITEMS_IMPORTER', 'ITEMS_IMPORTER_RESTRICTED'),
		canExport: () => useExport && hasAnyPermission('ITEMS_EXPORTER', 'ITEMS_EXPORTER_RESTRICTED'),
		canReserve: (item?: Pick<Item, 'canReserve'>) =>
			!!canReserve(true) && (item ? item?.canReserve === 'available' : true),
		canCheckout: (item?: Pick<Item, 'canOrder'>) =>
			!!canCheckout(true) && (item ? item?.canOrder === 'available' : true),
		canRetire: (item?: Pick<Item, 'status'>) => !!canAdmin() && (item ? item?.status !== 'expired' : true),
		canUnretire: (item?: Pick<Item, 'status'>) => !!canAdmin() && (item ? item?.status === 'expired' : true),
		canDelete: (item?: Pick<Item, 'status'>) =>
			!!canAdmin() && (item ? item?.status === 'available' || item?.status === 'expired' : true),

		canGoToCheckout: (item: Pick<Item, 'status' | 'kind'>) =>
			(item?.status === 'available' || item?.status === 'expired') && item.kind === 'simple',

		canDuplicate: canAdmin,

		canAddToKit: (item?: Pick<Item, 'kit'>) => !!canAdminKit(permissions) && (item ? !item?.kit : true),
		canUpdateKitQuantity: (item?: Pick<Item, 'kit' | 'inKits'>) =>
			!!canAdminKit(permissions) && (item ? !!item?.kit || !!item?.inKits : true),
		canRemoveFromKit: (item?: Pick<Item, 'kit' | 'inKits'>) =>
			!!canAdminKit(permissions) && (item ? !!item?.kit || !!item?.inKits : true),

		canTakeCustody: (item?: Pick<Item, 'canCustody'> & { custody: string }) =>
			canTakeItemCustody(permissions) && (item ? item?.canCustody === 'available' : true),
		canTransferCustody: (own?: boolean) => canTransferItemCustody(permissions, own),
		// Giving custody picks somebody else, so whose custody the item is in does not matter.
		canGiveCustody: (_item?: Pick<Item, 'canCustody'> & { custody: string }) => canGiveItemCustody(permissions),
		canReleaseCustody: (item?: Pick<Item, 'canCustody'> & { custody: string }) =>
			canReleaseItemCustody(permissions, item ? isOwn(item?.custody) : false),
		canReleaseCustodyAt: (item?: Pick<Item, 'canCustody'> & { custody: string }) =>
			itemPermissions.canReleaseCustody(item) && useReleaseAtLocation,
		canChangeFlag: () => useFlags && hasAnyPermission('ITEMS_FLAGGER', 'ITEMS_FLAGGER_RESTRICTED'),
		canClearFlag: () => useFlags && hasAnyPermission('ITEMS_UNFLAGGER', 'ITEMS_UNFLAGGER_RESTRICTED'),
		canChangeCover: canAdmin,
		canUpdatePermissions: canAdmin,
		canAddAttachment: () => hasAnyPermission('ITEMS_ATTACHMENTS_OWN_WRITER'),
		canRemoveAttachment: (isOwn = false) =>
			hasAnyPermission('ITEMS_ATTACHMENTS_DELETER') ||
			(isOwn && hasAnyPermission('ITEMS_ATTACHMENTS_OWN_DELETER')),
		canAddComment: () => hasAnyPermission('ITEMS_COMMENTS_OWN_WRITER'),
		canEditComment: (isOwnComment = false) => isOwnComment && hasAnyPermission('ITEMS_COMMENTS_OWN_WRITER'),
		canRemoveComment: (isOwnComment = false) =>
			hasAnyPermission('ITEMS_COMMENTS_DELETER') ||
			(isOwnComment && hasAnyPermission('ITEMS_COMMENTS_OWN_DELETER')),
		// GEO add-on + ITEMS_GEO_ADMIN*, as legacy and python-api `updateGeo`; not the item admin right.
		canUpdateGeo: () => canUpdateItemGeo(permissions),
		canClearCodes: canAdmin,
		canGenerateCodes: canAdmin,
		// Stricter than python-api on purpose: `getDepreciation` only needs the depreciations add-on +
		// ITEMS_READER*, but depreciation (purchase value) is admin information, so the UI shows it to
		// item admins (ITEMS_ADMIN*) only.
		canViewItemDepreciation: () => !!useDepreciations && canAdmin(),
	};

	return itemPermissions;
};

export const useItemPermissions = (props: PermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(() => createItemPermissions(permissions), [permissions]);
};
